'use client';

import React from 'react';
import { CheckCircle } from 'lucide-react';

interface LicenseOption {
  key: string;
  value: string;
  label: string;
  shortDesc: string;
  fullDesc: string;
  color: string;
  bgColor: string;
  borderColor: string;
}

const licenses: LicenseOption[] = [
  {
    key: 'lic-cc0',
    value: 'CC0',
    label: 'CC0 — Public Domain',
    shortDesc: 'No rights reserved',
    fullDesc: 'Anyone can use, modify, and distribute — even commercially — without attribution.',
    color: '#22C55E',
    bgColor: 'rgba(34, 197, 94, 0.08)',
    borderColor: 'rgba(34, 197, 94, 0.3)',
  },
  {
    key: 'lic-ccby',
    value: 'CC BY',
    label: 'CC BY — Attribution',
    shortDesc: 'Credit required',
    fullDesc: 'Free to use commercially, but you must credit the original creator.',
    color: '#F59E0B',
    bgColor: 'rgba(245, 158, 11, 0.08)',
    borderColor: 'rgba(245, 158, 11, 0.3)',
  },
  {
    key: 'lic-ccbysa',
    value: 'CC BY-SA',
    label: 'CC BY-SA — ShareAlike',
    shortDesc: 'Credit + share alike',
    fullDesc: 'Must credit creator and share derivatives under the same license.',
    color: '#3B9EFF',
    bgColor: 'rgba(59, 158, 255, 0.08)',
    borderColor: 'rgba(59, 158, 255, 0.3)',
  },
  {
    key: 'lic-personal',
    value: 'Personal',
    label: 'Personal Use Only',
    shortDesc: 'No commercial use',
    fullDesc: 'Editors may use for personal, non-commercial projects only. No redistribution.',
    color: '#EF4444',
    bgColor: 'rgba(239, 68, 68, 0.08)',
    borderColor: 'rgba(239, 68, 68, 0.3)',
  },
];

interface LicenseSelectorProps {
  selected: string;
  onSelect: (value: string) => void;
}

export default function LicenseSelector({ selected, onSelect }: LicenseSelectorProps) {
  return (
    <div className="space-y-2">
      {licenses.map((lic) => {
        const isActive = selected === lic.value;
        return (
          <button
            key={lic.key}
            type="button"
            onClick={() => onSelect(lic.value)}
            className="w-full text-left rounded-xl p-3 transition-all duration-150 hover:scale-[1.01]"
            style={{
              background: isActive ? lic.bgColor : 'var(--muted)',
              border: `1px solid ${isActive ? lic.borderColor : 'var(--border)'}`,
            }}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-bold" style={{ color: isActive ? lic.color : 'var(--foreground)' }}>
                    {lic.label}
                  </span>
                </div>
                <p className="text-[11px] leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                  {isActive ? lic.fullDesc : lic.shortDesc}
                </p>
              </div>
              <span
                className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-150"
                style={{
                  background: isActive ? lic.bgColor : 'var(--border)',
                  border: `2px solid ${isActive ? lic.color : 'var(--border)'}`,
                }}
              >
                {isActive && <CheckCircle size={12} style={{ color: lic.color }} />}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}